/**
 * The feedback button, and the triage list behind it.
 *
 * Deliberately always reachable rather than buried in a settings screen: the
 * moment something is annoying is the moment it gets described accurately, and
 * a note written three days later is a shrug.
 *
 * The screen she was on and the exact build are attached automatically. She
 * should type the sentence and nothing else.
 */
import { useCallback, useEffect, useState } from 'react'
import Icon from '../Icon'
import { Empty } from './Bits'
import { get, patch, post } from '../../lib/api'

export const FEEDBACK_STATUSES = ['new', 'planned', 'doing', 'done', 'declined'] as const
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number]

export const STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: 'Not looked at',
  planned: 'Worth doing',
  doing: 'In progress',
  done: 'Done',
  declined: 'Not doing',
}

export interface Note {
  id: string
  message: string
  screen: string | null
  recipeId: string | null
  recipeTitle: string | null
  appVersion: string | null
  commit: string | null
  environment: string | null
  status: FeedbackStatus
  rank: number | null
  decision: string | null
  decidedAt: number | null
  createdAt: number
  who: string | null
}

/* ── the button ─────────────────────────────────────────────────────────── */

export default function FeedbackButton({ screen, recipeId }: { screen: string; recipeId?: string | null }) {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const send = async () => {
    if (message.trim().length < 3 || busy) return
    setBusy(true); setError(null)
    try {
      await post('/api/feedback', { message: message.trim(), screen, recipeId: recipeId ?? null })
      setSent(true)
      setMessage('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not send.')
    } finally { setBusy(false) }
  }

  const close = () => { setOpen(false); setSent(false); setError(null) }

  return (
    <>
      <button type="button" className="feedback-tab" onClick={() => setOpen(true)}>
        <Icon name="speech" size={15} /> Feedback
      </button>

      {open && (
        <div className="sheet-backdrop" onClick={close}>
          <div
            className="sheet" role="dialog" aria-modal="true" aria-label="Send feedback"
            onClick={(e) => e.stopPropagation()}
          >
            {sent ? (
              <>
                <h2>Got it — thank you.</h2>
                <p className="muted">
                  It is on the list. Nothing disappears: every note gets looked at and either
                  planned, done, or turned down with a reason.
                </p>
                <button type="button" className="primary" onClick={close}>Close</button>
              </>
            ) : (
              <>
                <h2>What&rsquo;s on your mind?</h2>
                <p className="muted small">
                  Anything — a bug, something confusing, something you wish it did.
                  {/* Said plainly so she does not waste words describing context we already have. */}
                  {' '}We already know you were on <b>{screen}</b> and which version you&rsquo;re running.
                </p>
                <textarea
                  value={message} onChange={(e) => setMessage(e.target.value)} rows={6}
                  autoFocus placeholder="The timer kept…"
                  aria-label="Your feedback"
                />
                {error && <p className="error">{error}</p>}
                <div className="filter-row">
                  <button type="button" className="primary" style={{ width: 'auto' }} disabled={busy || message.trim().length < 3} onClick={send}>
                    {busy ? 'Sending…' : 'Send it'}
                  </button>
                  <button type="button" className="ghost" onClick={close}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}

/* ── the triage list ────────────────────────────────────────────────────── */

/**
 * The half that makes the button worth having.
 *
 * Built for a session where the two of you go through the list together:
 * decide worth-doing or not, put the worthwhile ones in an order, and record
 * WHY for the rest — because "we decided not to" with a reason is a finished
 * item, and an unanswered note is not.
 */
export function Triage() {
  const [notes, setNotes] = useState<Note[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [filter, setFilter] = useState<FeedbackStatus | ''>('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await get<{ notes: Note[]; counts: Record<string, number> }>(
        `/api/feedback${filter ? `?status=${filter}` : ''}`,
      )
      setNotes(r.notes)
      if (!filter) setCounts(r.counts)
    } finally { setLoading(false) }
  }, [filter])

  useEffect(() => { void load() }, [load])

  const update = async (id: string, body: Record<string, unknown>) => {
    const { note } = await patch<{ note: Note }>(`/api/feedback/${id}`, body)
    setNotes((held) => held.map((n) => (n.id === id ? note : n)))
    if (!filter) void load()
  }

  if (loading && !notes.length) return <p className="muted">Loading…</p>

  return (
    <>
      <div className="filters">
        <div className="filter-row">
          <button type="button" aria-current={filter === ''} onClick={() => setFilter('')}>
            Everything {!!notes.length && <span className="muted">{Object.values(counts).reduce((a, b) => a + b, 0)}</span>}
          </button>
          {FEEDBACK_STATUSES.map((s) => (
            <button key={s} type="button" aria-current={filter === s} onClick={() => setFilter(s)}>
              {STATUS_LABEL[s]} {counts[s] ? <span className="muted">{counts[s]}</span> : null}
            </button>
          ))}
        </div>
      </div>

      {!notes.length ? (
        <Empty icon="book" title="Nothing here">
          <p>{filter ? 'Nothing with that status.' : 'No feedback yet. The button is bottom-left on every screen.'}</p>
        </Empty>
      ) : (
        <ul className="list triage">
          {notes.map((n) => (
            <li key={n.id} className={`triage-item is-${n.status}`}>
              <div className="triage-body">
                <p className="triage-message">{n.message}</p>
                <div className="triage-meta">
                  {n.who && <span>{n.who}</span>}
                  <span>{new Date(n.createdAt).toLocaleDateString()}</span>
                  {n.screen && <span>on <b>{n.screen}</b></span>}
                  {n.recipeTitle && <span>· {n.recipeTitle}</span>}
                  {/* The build it was reported against. A report on a version
                      that no longer exists is history, not a bug. */}
                  {n.commit && <span className="mono">{n.commit}</span>}
                  {n.environment && <span className="mono">{n.environment}</span>}
                </div>
                {n.decision && <p className="triage-decision"><b>Decided:</b> {n.decision}</p>}
              </div>

              <div className="triage-controls">
                <select
                  value={n.status} aria-label="Status"
                  onChange={(e) => void update(n.id, { status: e.target.value })}
                >
                  {FEEDBACK_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
                <input
                  type="number" inputMode="numeric" placeholder="order"
                  aria-label="Order" defaultValue={n.rank ?? ''}
                  onBlur={(e) => {
                    const v = e.target.value
                    if (String(n.rank ?? '') !== v) void update(n.id, { rank: v === '' ? null : Number(v) })
                  }}
                />
                <input
                  type="text" placeholder="why — for the version of us in a month"
                  aria-label="Decision" defaultValue={n.decision ?? ''}
                  onBlur={(e) => {
                    if ((n.decision ?? '') !== e.target.value) void update(n.id, { decision: e.target.value })
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
